import { createElement, Fragment, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { ArrowDownTrayIcon, EyeIcon, PlusIcon, CheckCircleIcon, XCircleIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

interface Partner {
  id: number;
  name: string;
  slug: string;
  email: string;
  company_name: string | null;
  commission_rate: number;
  commission_duration_months: number;
  status: string;
  signup_count: number;
  conversion_count: number;
  total_revenue_cents: number;
  total_commission_cents: number;
  total_commission_paid_cents: number;
  commission_owed_cents: number;
  refund_count: number;
  created_at: string;
}

interface PartnerApplication {
  id: number;
  name: string;
  email: string;
  company_name: string | null;
  website_url: string | null;
  partner_type: string | null;
  referral_plan: string | null;
  status: string;
  created_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  prev_page: number | null;
  next_page: number | null;
}

interface Stats {
  total_partners: number;
  active_partners: number;
  pending_applications: number;
  total_signups: number;
  total_conversions: number;
  total_revenue_cents: number;
  total_commission_cents: number;
  total_commission_paid_cents: number;
  total_commission_owed_cents: number;
}

interface PartnersIndexProps {
  partners: Partner[];
  partners_pagination: Pagination;
  applications: PartnerApplication[];
  applications_pagination: Pagination;
  stats: Stats;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) {
    const m = dollars / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (dollars >= 10_000) {
    const k = dollars / 1_000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `$${dollars.toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    active: "bg-green-100 text-green-800",
    inactive: "bg-red-100 text-red-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-500"}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function PaginationControls({
  pagination,
  pageParam,
}: {
  pagination: Pagination;
  pageParam: string;
}) {
  if (pagination.total_pages <= 1) return null;

  const navigate = (page: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set(pageParam, String(page));
    router.visit(url.toString(), { preserveState: true, preserveScroll: true });
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
      <span className="text-sm text-muted-foreground">
        Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} total)
      </span>
      <div className="flex gap-2">
        <button
          onClick={() => navigate(pagination.current_page - 1)}
          disabled={!pagination.prev_page}
          className="px-3 py-1 text-sm rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => navigate(pagination.current_page + 1)}
          disabled={!pagination.next_page}
          className="px-3 py-1 text-sm rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function ApproveForm({ application, onDone }: { application: PartnerApplication; onDone: () => void }) {
  const [commissionRate, setCommissionRate] = useState("25.0");
  const [durationMonths, setDurationMonths] = useState("3");
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = () => {
    setSubmitting(true);
    router.patch(`/admin/partner_applications/${application.id}/approve`, {
      commission_rate: parseFloat(commissionRate),
      commission_duration_months: parseInt(durationMonths, 10),
    }, { onFinish: () => { setSubmitting(false); onDone(); } });
  };

  return (
    <tr className="bg-green-50/50">
      <td colSpan={7} className="px-4 py-3">
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Commission:</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0"
              max="30"
              step="0.5"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              className="w-20 px-2 py-1 text-sm border border-border rounded bg-background"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
          <span className="text-sm text-muted-foreground">for</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="1"
              max="24"
              value={durationMonths}
              onChange={(e) => setDurationMonths(e.target.value)}
              className="w-16 px-2 py-1 text-sm border border-border rounded bg-background"
            />
            <span className="text-sm text-muted-foreground">months</span>
          </div>
          <button
            onClick={handleApprove}
            disabled={submitting}
            className="px-3 py-1 text-sm font-medium rounded bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {submitting ? "Approving..." : "Confirm Approve"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function PartnersIndex({ partners, partners_pagination, applications, applications_pagination, stats }: PartnersIndexProps) {
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());

  const visibleApplications = applications.filter((app) => !removedIds.has(app.id));

  const handleReject = (app: PartnerApplication) => {
    if (confirm(`Reject application from ${app.name}?`)) {
      setRemovedIds((prev) => new Set(prev).add(app.id));
      router.patch(`/admin/partner_applications/${app.id}/reject`);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Partners</h1>
          <p className="text-muted-foreground mt-1">
            Manage partner referral program
          </p>
        </div>
        <div className="flex gap-2">
          {partners.length > 0 && (
            <a
              href="/admin/partners/export"
              download="partners.csv"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export CSV
            </a>
          )}
          <button
            onClick={() => router.visit("/admin/partner_payouts")}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted transition-colors"
          >
            <BanknotesIcon className="w-4 h-4" />
            Payouts
          </button>
          <Link
            href="/admin/partners/new"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            New Partner
          </Link>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
        <StatCard label="Total Partners" value={stats.total_partners} />
        <StatCard label="Active" value={stats.active_partners} />
        <StatCard label="Pending" value={stats.pending_applications} />
        <StatCard label="Signups" value={stats.total_signups} />
        <StatCard label="Conversions" value={stats.total_conversions} />
        <StatCard label="Revenue" value={formatCents(stats.total_revenue_cents)} />
        <StatCard label="Commission This Month" value={formatCents(stats.total_commission_owed_cents)} />
      </div>

      {/* Applications Table */}
      {visibleApplications.length > 0 && (
        <div className="bg-card rounded-lg border border-amber-200 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-amber-200 bg-amber-50/50">
            <h2 className="text-lg font-semibold text-foreground">
              Pending Applications ({visibleApplications.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Email</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Company</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Type</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Applied</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleApplications.map((app) => (
                  <Fragment key={app.id}>
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground">{app.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{app.email}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{app.company_name || "—"}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{app.partner_type || "—"}</td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={app.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(app.created_at)}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button
                          onClick={() => setApprovingId(approvingId === app.id ? null : app.id)}
                          className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-500 transition-colors"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(app)}
                          className="inline-flex items-center gap-1 text-sm text-red-500 hover:text-red-400 transition-colors"
                        >
                          <XCircleIcon className="w-4 h-4" />
                          Reject
                        </button>
                      </td>
                    </tr>
                    {approvingId === app.id && (
                      <ApproveForm
                        application={app}
                        onDone={() => {
                          setApprovingId(null);
                          setRemovedIds((prev) => new Set(prev).add(app.id));
                        }}
                      />
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls pagination={applications_pagination} pageParam="applications_page" />
        </div>
      )}

      {/* Partners Table */}
      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            Partners ({partners_pagination.total_count})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  Slug
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                  Commission
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                  Signups
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                  Conversions
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                  Refunds
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                  Revenue
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                  Commission Paid
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                  Commission This Month
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  Created
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {partners.map((partner) => (
                <tr
                  key={partner.id}
                  className="hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-medium text-foreground">
                        {partner.name}
                      </span>
                      {partner.company_name && (
                        <div className="text-xs text-muted-foreground">
                          {partner.company_name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground font-mono">
                    {partner.slug}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-muted-foreground">
                    {partner.commission_rate}% / {partner.commission_duration_months}mo
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={partner.status} />
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-foreground">
                    {partner.signup_count}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-foreground">
                    {partner.conversion_count}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-foreground">
                    {partner.refund_count}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-foreground">
                    {formatCents(partner.total_revenue_cents)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-foreground">
                    {formatCents(partner.total_commission_paid_cents)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-foreground">
                    {formatCents(partner.commission_owed_cents)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(partner.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      href={`/admin/partners/${partner.id}`}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </Link>
                  </td>
                </tr>
              ))}
              {partners.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No partners yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls pagination={partners_pagination} pageParam="partners_page" />
      </div>
    </div>
  );
}

PartnersIndex.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default PartnersIndex;
